import React, { useState } from "react";
import { Result, Typography, Space, Button } from "antd";
import {
  UserOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import LogoutButton from "../LogoutButtom/LogoutButton";
import styles from "./PatientDataRequired.module.scss";

const { Title, Paragraph } = Typography;

interface PatientDataRequiredProps {
  errorMessage?: string;
  onRetry?: () => void;
}

/**
 * Componente que se muestra cuando el paciente no tiene datos registrados en el sistema.
 * Bloquea toda la aplicación hasta que se registren los datos del paciente.
 */
const PatientDataRequired: React.FC<PatientDataRequiredProps> = ({
  onRetry,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    if (onRetry) {
      setIsRetrying(true);
      try {
        await onRetry();
      } finally {
        // El componente se desmontará si el retry es exitoso
        // Solo quitamos el loading si aún estamos montados
        setTimeout(() => setIsRetrying(false), 1000);
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.logoutButtonTop}>
          <LogoutButton />
        </div>
        <Result
          status="warning"
          icon={
            <div className={styles.iconContainer}>
              <ExclamationCircleOutlined className={styles.warningIcon} />
              <UserOutlined className={styles.userIcon} />
            </div>
          }
          title={
            <Title level={2} className={styles.title}>
              Datos de Paciente No Registrados
            </Title>
          }
          subTitle={
            <Space direction="vertical" size="large" className={styles.info}>
              <Paragraph className={styles.mainMessage}>
                Lo sentimos, pero no encontramos tus datos personales
                registrados en nuestro sistema.
              </Paragraph>

              <div className={styles.instructionsBox}>
                <Title level={4} className={styles.instructionsTitle}>
                  ¿Qué debes hacer?
                </Title>
                <Paragraph className={styles.instructions}>
                  Por favor, contacta al equipo de staff de la clínica para que
                  puedan agregar tu información personal al sistema.
                </Paragraph>
                <Paragraph className={styles.instructions}>
                  Una vez que tus datos sean registrados, podrás acceder a todas
                  las funcionalidades de la aplicación.
                </Paragraph>
              </div>

              <div className={styles.contactBox}>
                <Title level={5} className={styles.contactTitle}>
                  Información de Contacto
                </Title>
                <Paragraph className={styles.contactInfo}>
                  Dirígete a la recepción de la clínica o comunícate con nuestro
                  personal de atención al paciente para solicitar el registro de
                  tus datos.
                </Paragraph>
              </div>

              {onRetry && (
                <div className={styles.retrySection}>
                  <Paragraph className={styles.retryText}>
                    Si el staff ya registró tus datos, haz clic en el siguiente
                    botón para verificar nuevamente:
                  </Paragraph>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ReloadOutlined spin={isRetrying} />}
                    onClick={handleRetry}
                    loading={isRetrying}
                    className={styles.retryButton}
                  >
                    {isRetrying ? "Verificando..." : "Verificar Nuevamente"}
                  </Button>
                </div>
              )}

              <div className={styles.logoutSection}>
                <Paragraph className={styles.logoutText}>
                  ¿Necesitas cerrar sesión?
                </Paragraph>
                <Paragraph className={styles.logoutSubText}>
                  Haz clic en el avatar de usuario en la esquina superior
                  derecha.
                </Paragraph>
              </div>
            </Space>
          }
        />
      </div>
    </div>
  );
};

export default PatientDataRequired;
